import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { Grid, TextField, Typography } from '@material-ui/core'
import CheckIcon from '@material-ui/icons/Check'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import { Page, Header, CustomButton, SecaoGenerica, ModalConfirmacao, SelecionarData } from 'components'
import { cutMaxLength } from 'utils/helpers'
import { SelectUsuarios } from 'components/SelectUsuarios'
import AutocompleteNative from '@material-ui/lab/Autocomplete'
import moment from 'moment'
import { useParams } from 'react-router'

import { useInumarCadaver } from './hooks/useInumarCadaver'
import useStyles from './InumarCadaver.styles'

const maxLength250 = (newValue) => cutMaxLength(newValue, 250)
const maxLength4000 = (newValue) => cutMaxLength(newValue, 4000)

export const InumarCadaver = (props) => {
  const { route } = props
  const params = useParams()
  const classes = useStyles()

  const {
    formValues,
    formErrors,
    agentes,
    processandoOperacao,
    espelhoAberto,
    setEspelhoAberto,
    handleChangeCampoData,
    handleChangeNewValueByNameProperty,
    handleChangeWithModification,
    handleChangeAgentes,
    confirmarOperacao,
    hasError,
    router,
  } = useInumarCadaver({ route, idCadaver: params.id })

  const handleClickBotaoConfirmar = useCallback(() => {
    setEspelhoAberto(true)
  }, [setEspelhoAberto])

  return (
    <Page className={classes.root} title={`${route.titulo} - SIGEP`}>
      <Header titulo={route.titulo} overline={route.secao} />

      <Grid container spacing={2}>
        {/* Ficha de Inumação - Renderizada de forma plana sem SecaoGenerica e sem título de bloco */}
        <Grid item md={6} sm={6} xs={12} className={classes.campoFormulario}>
          <SelecionarData
            dataBD={formValues.dataObito}
            withoutTime
            name="dataObito"
            callback={(dataObito) => handleChangeCampoData('dataObito', dataObito)}
            label="DATA DO ÓBITO"
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid item md={6} sm={6} xs={12} className={classes.campoFormulario}>
          <SelecionarData
            dataBD={formValues.dataInumacao}
            withoutTime
            name="dataInumacao"
            callback={(dataInumacao) => handleChangeCampoData('dataInumacao', dataInumacao)}
            label="DATA DA INUMAÇÃO"
            required
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid item md={12} sm={12} xs={12} className={classes.campoFormulario}>
          <TextField
            value={formValues.cemiterio || ''}
            name="cemiterio"
            error={hasError('cemiterio')}
            variant="outlined"
            label="CEMITÉRIO"
            helperText={hasError('cemiterio') ? formErrors.cemiterio[0] : null}
            fullWidth
            required
            onChange={(e) => handleChangeWithModification(e, maxLength250)}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <Typography variant="caption">
            Caracteres restantes: {250 - (formValues.cemiterio?.length ? formValues.cemiterio?.length : 0)}
          </Typography>
        </Grid>

        <Grid item md={12} sm={12} xs={12} className={classes.campoFormulario}>
          <TextField
            error={hasError('localizacao')}
            fullWidth
            multiline
            rows={5}
            helperText={hasError('localizacao') ? formErrors.localizacao[0] : null}
            label="LOCALIZAÇÃO INTERNA NO CEMITÉRIO"
            name="localizacao"
            variant="outlined"
            onChange={(e) => handleChangeWithModification(e, maxLength4000)}
            value={formValues.localizacao || ''}
            required
            InputLabelProps={{
              shrink: true,
            }}
          />
          <Typography variant="caption">
            Caracteres restantes: {4000 - (formValues.localizacao?.length ? formValues.localizacao?.length : 0)}
          </Typography>
        </Grid>

        {/* ADMINISTRADOR DO CEMITÉRIO - SecaoGenerica */}
        <Grid item md={12} sm={12} xs={12}>
          <SecaoGenerica titulo="ADMINISTRADOR DO CEMITÉRIO">
            <Grid container spacing={2}>
              <Grid item md={12} sm={12} xs={12} className={classes.campoFormulario}>
                <TextField
                  error={hasError('cemiterioAdm')}
                  fullWidth
                  multiline
                  helperText={hasError('cemiterioAdm') ? formErrors.cemiterioAdm[0] : null}
                  label="ADMINISTRADOR DO CEMITÉRIO"
                  name="cemiterioAdm"
                  variant="outlined"
                  onChange={(e) => handleChangeWithModification(e, maxLength250)}
                  value={formValues.cemiterioAdm || ''}
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <Typography variant="caption">
                  Caracteres restantes: {250 - (formValues.cemiterioAdm?.length ? formValues.cemiterioAdm?.length : 0)}
                </Typography>
              </Grid>

              <Grid item md={4} sm={4} xs={12} className={classes.campoFormulario}>
                <AutocompleteNative
                  name="cemiterioAdmTipoDoc"
                  noOptionsText="Sem informações"
                  options={[
                    'REGISTRO CIVIL',
                    'CPF',
                    'CNH',
                    'CARTEIRA DE TRABALHO',
                    'CARTEIRA PROFISSIONAL',
                    'PASSAPORTE',
                    'CARTEIRA DE IDENTIFICAÇÃO FUNCIONAL',
                    'OUTRO',
                  ]}
                  value={formValues.cemiterioAdmTipoDoc || null}
                  onChange={(event, newValue) => {
                    handleChangeNewValueByNameProperty(event, newValue, 'cemiterioAdmTipoDoc')
                  }}
                  getOptionLabel={(option) => option}
                  renderOption={(option) => option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="TIPO DE DOCUMENTO"
                      error={hasError('cemiterioAdmTipoDoc')}
                      helperText={hasError('cemiterioAdmTipoDoc') ? formErrors.cemiterioAdmTipoDoc[0] : null}
                      required
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item md={4} sm={4} xs={12} className={classes.campoFormulario}>
                <TextField
                  error={hasError('cemiterioAdmDoc')}
                  fullWidth
                  multiline
                  helperText={hasError('cemiterioAdmDoc') ? formErrors.cemiterioAdmDoc[0] : null}
                  label="DOCUMENTO DO ADMINISTRADOR"
                  name="cemiterioAdmDoc"
                  variant="outlined"
                  onChange={(e) => handleChangeWithModification(e, maxLength250)}
                  value={formValues.cemiterioAdmDoc || ''}
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item md={4} sm={4} xs={12} className={classes.campoFormulario}>
                <TextField
                  error={hasError('orgaoExpedidorCemiterioAdm')}
                  fullWidth
                  multiline
                  helperText={
                    hasError('orgaoExpedidorCemiterioAdm') ? formErrors.orgaoExpedidorCemiterioAdm[0] : null
                  }
                  label="ÓRGÃO EXPEDIDOR"
                  name="orgaoExpedidorCemiterioAdm"
                  variant="outlined"
                  onChange={(e) => handleChangeWithModification(e, maxLength250)}
                  value={formValues.orgaoExpedidorCemiterioAdm || ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </SecaoGenerica>
        </Grid>

        {/* AGENTES RESPONSÁVEIS - SecaoGenerica */}
        <Grid item md={12} sm={12} xs={12}>
          <SecaoGenerica titulo="AGENTES RESPONSÁVEIS">
            <Grid container spacing={2}>
              <Grid className={classes.campoFormulario} item md={12} sm={12} xs={12}>
                <SelectUsuarios
                  usuariosExternos={agentes}
                  setUsuariosExternos={handleChangeAgentes}
                  label="SELECIONE OS AGENTES"
                  isFromBanco={false}
                  multiplo={true}
                />
              </Grid>
            </Grid>
          </SecaoGenerica>
        </Grid>

        {/* TESTEMUNHA 1 - SecaoGenerica */}
        <Grid item md={12} sm={12} xs={12}>
          <SecaoGenerica titulo="TESTEMUNHA 1">
            <Grid container spacing={2}>
              <Grid item md={12} sm={12} xs={12} className={classes.campoFormulario}>
                <TextField
                  error={hasError('testemunhaA')}
                  fullWidth
                  multiline
                  helperText={hasError('testemunhaA') ? formErrors.testemunhaA[0] : null}
                  label="NOME COMPLETO"
                  name="testemunhaA"
                  variant="outlined"
                  onChange={(e) => handleChangeWithModification(e, maxLength250)}
                  value={formValues.testemunhaA || ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <Typography variant="caption">
                  Caracteres restantes: {250 - (formValues.testemunhaA?.length ? formValues.testemunhaA?.length : 0)}
                </Typography>
              </Grid>

              <Grid item md={4} sm={4} xs={12} className={classes.campoFormulario}>
                <AutocompleteNative
                  name="testemunhaATipoDoc"
                  noOptionsText="Sem informações"
                  options={[
                    'REGISTRO CIVIL',
                    'CPF',
                    'CNH',
                    'CARTEIRA DE TRABALHO',
                    'CARTEIRA PROFISSIONAL',
                    'PASSAPORTE',
                    'CARTEIRA DE IDENTIFICAÇÃO FUNCIONAL',
                    'OUTRO',
                  ]}
                  value={formValues.testemunhaATipoDoc || null}
                  onChange={(event, newValue) => {
                    handleChangeNewValueByNameProperty(event, newValue, 'testemunhaATipoDoc')
                  }}
                  getOptionLabel={(option) => option}
                  renderOption={(option) => option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="TIPO DE DOCUMENTO"
                      error={hasError('testemunhaATipoDoc')}
                      helperText={hasError('testemunhaATipoDoc') ? formErrors.testemunhaATipoDoc[0] : null}
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item md={4} sm={4} xs={12} className={classes.campoFormulario}>
                <TextField
                  error={hasError('testemunhaADoc')}
                  fullWidth
                  multiline
                  helperText={hasError('testemunhaADoc') ? formErrors.testemunhaADoc[0] : null}
                  label="DOCUMENTO DA TESTEMUNHA"
                  name="testemunhaADoc"
                  variant="outlined"
                  onChange={(e) => handleChangeWithModification(e, maxLength250)}
                  value={formValues.testemunhaADoc || ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item md={4} sm={4} xs={12} className={classes.campoFormulario}>
                <TextField
                  error={hasError('testemunhaAOrgaoExpedidor')}
                  fullWidth
                  multiline
                  helperText={hasError('testemunhaAOrgaoExpedidor') ? formErrors.testemunhaAOrgaoExpedidor[0] : null}
                  label="ÓRGÃO EXPEDIDOR"
                  name="testemunhaAOrgaoExpedidor"
                  variant="outlined"
                  onChange={(e) => handleChangeWithModification(e, maxLength250)}
                  value={formValues.testemunhaAOrgaoExpedidor || ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </SecaoGenerica>
        </Grid>

        {/* TESTEMUNHA 2 - SecaoGenerica */}
        <Grid item md={12} sm={12} xs={12}>
          <SecaoGenerica titulo="TESTEMUNHA 2">
            <Grid container spacing={2}>
              <Grid item md={12} sm={12} xs={12} className={classes.campoFormulario}>
                <TextField
                  error={hasError('testemunhaB')}
                  fullWidth
                  multiline
                  helperText={hasError('testemunhaB') ? formErrors.testemunhaB[0] : null}
                  label="NOME COMPLETO"
                  name="testemunhaB"
                  variant="outlined"
                  onChange={(e) => handleChangeWithModification(e, maxLength250)}
                  value={formValues.testemunhaB || ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <Typography variant="caption">
                  Caracteres restantes: {250 - (formValues.testemunhaB?.length ? formValues.testemunhaB?.length : 0)}
                </Typography>
              </Grid>

              <Grid item md={4} sm={4} xs={12} className={classes.campoFormulario}>
                <AutocompleteNative
                  name="testemunhaBTipoDoc"
                  noOptionsText="Sem informações"
                  options={[
                    'REGISTRO CIVIL',
                    'CPF',
                    'CNH',
                    'CARTEIRA DE TRABALHO',
                    'CARTEIRA PROFISSIONAL',
                    'PASSAPORTE',
                    'CARTEIRA DE IDENTIFICAÇÃO FUNCIONAL',
                    'OUTRO',
                  ]}
                  value={formValues.testemunhaBTipoDoc || null}
                  onChange={(event, newValue) => {
                    handleChangeNewValueByNameProperty(event, newValue, 'testemunhaBTipoDoc')
                  }}
                  getOptionLabel={(option) => option}
                  renderOption={(option) => option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="TIPO DE DOCUMENTO"
                      error={hasError('testemunhaBTipoDoc')}
                      helperText={hasError('testemunhaBTipoDoc') ? formErrors.testemunhaBTipoDoc[0] : null}
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item md={4} sm={4} xs={12} className={classes.campoFormulario}>
                <TextField
                  error={hasError('testemunhaBDoc')}
                  fullWidth
                  multiline
                  helperText={hasError('testemunhaBDoc') ? formErrors.testemunhaBDoc[0] : null}
                  label="DOCUMENTO DA TESTEMUNHA"
                  name="testemunhaBDoc"
                  variant="outlined"
                  onChange={(e) => handleChangeWithModification(e, maxLength250)}
                  value={formValues.testemunhaBDoc || ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item md={4} sm={4} xs={12} className={classes.campoFormulario}>
                <TextField
                  error={hasError('testemunhaBOrgaoExpedidor')}
                  fullWidth
                  multiline
                  helperText={hasError('testemunhaBOrgaoExpedidor') ? formErrors.testemunhaBOrgaoExpedidor[0] : null}
                  label="ÓRGÃO EXPEDIDOR"
                  name="testemunhaBOrgaoExpedidor"
                  variant="outlined"
                  onChange={(e) => handleChangeWithModification(e, maxLength250)}
                  value={formValues.testemunhaBOrgaoExpedidor || ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </SecaoGenerica>
        </Grid>

        {/* Ações / Botões inferiores */}
        <Grid item md={12} sm={12} xs={12}>
          <div className={classes.botoesFormulario}>
            <div className={classes.botaoContainer}>
              <CustomButton
                colorType="primary"
                variant="outlined"
                size="medium"
                onClick={() => router.history.goBack()}
                startIcon={<ArrowBackIcon />}
              >
                Voltar
              </CustomButton>
            </div>

            <div className={classes.botaoContainer}>
              <CustomButton
                colorType="secondary"
                variant="contained"
                size="medium"
                disabled={processandoOperacao || Object.keys(formErrors).length > 0}
                isLoading={processandoOperacao}
                onClick={handleClickBotaoConfirmar}
                endIcon={<CheckIcon />}
              >
                CADASTRAR
              </CustomButton>
            </div>
          </div>
        </Grid>
      </Grid>

      {/* ModalConfirmacao (Substituto do Espelho) */}
      <ModalConfirmacao
        aberto={espelhoAberto}
        onClose={() => {
          if (!processandoOperacao) {
            setEspelhoAberto(false)
          }
        }}
        onConfirm={confirmarOperacao}
        processando={processandoOperacao}
        titulo="Confirmação de dados"
        confirmarLabel="Confirmar"
        cancelarLabel="Voltar"
        confirmarColor="secondary"
        maxWidth="md"
      >
        <Grid container spacing={2} className={classes.dadosEspelho}>
          <Grid item md={6} sm={6} xs={12}>
            <Typography variant="body2" color="textSecondary">
              <b>DATA DO ÓBITO</b>
            </Typography>
            <Typography variant="body1">
              {formValues.dataObito ? moment(formValues.dataObito).format('DD/MM/YYYY') : '-'}
            </Typography>
          </Grid>
          <Grid item md={6} sm={6} xs={12}>
            <Typography variant="body2" color="textSecondary">
              <b>DATA DA INUMAÇÃO</b>
            </Typography>
            <Typography variant="body1">
              {formValues.dataInumacao ? moment(formValues.dataInumacao).format('DD/MM/YYYY') : '-'}
            </Typography>
          </Grid>
          <Grid item md={6} sm={6} xs={12}>
            <Typography variant="body2" color="textSecondary">
              <b>CEMITÉRIO</b>
            </Typography>
            <Typography variant="body1">{formValues.cemiterio}</Typography>
          </Grid>
          <Grid item md={6} sm={6} xs={12}>
            <Typography variant="body2" color="textSecondary">
              <b>LOCALIZAÇÃO INTERNA NO CEMITÉRIO</b>
            </Typography>
            <Typography variant="body1">{formValues.localizacao}</Typography>
          </Grid>

          <Grid item md={12} sm={12} xs={12}>
            <Typography variant="body2" color="textSecondary">
              <b>ADMINISTRADOR DO CEMITÉRIO</b>
            </Typography>
            <Typography variant="body1">{formValues.cemiterioAdm}</Typography>
          </Grid>
          <Grid item md={4} sm={4} xs={12}>
            <Typography variant="body2" color="textSecondary">
              <b>TIPO DE DOCUMENTO</b>
            </Typography>
            <Typography variant="body1">{formValues.cemiterioAdmTipoDoc}</Typography>
          </Grid>
          <Grid item md={4} sm={4} xs={12}>
            <Typography variant="body2" color="textSecondary">
              <b>DOCUMENTO</b>
            </Typography>
            <Typography variant="body1">{formValues.cemiterioAdmDoc}</Typography>
          </Grid>
          <Grid item md={4} sm={4} xs={12}>
            <Typography variant="body2" color="textSecondary">
              <b>ÓRGÃO EXPEDIDOR</b>
            </Typography>
            <Typography variant="body1">{formValues.orgaoExpedidorCemiterioAdm || '-'}</Typography>
          </Grid>

          {formValues.testemunhaA && (
            <>
              <Grid item md={12} sm={12} xs={12}>
                <Typography variant="body2" color="textSecondary">
                  <b>TESTEMUNHA 1</b>
                </Typography>
                <Typography variant="body1">{formValues.testemunhaA}</Typography>
              </Grid>
              <Grid item md={4} sm={4} xs={12}>
                <Typography variant="body2" color="textSecondary">
                  <b>TIPO DE DOCUMENTO</b>
                </Typography>
                <Typography variant="body1">{formValues.testemunhaATipoDoc || '-'}</Typography>
              </Grid>
              <Grid item md={4} sm={4} xs={12}>
                <Typography variant="body2" color="textSecondary">
                  <b>DOCUMENTO</b>
                </Typography>
                <Typography variant="body1">{formValues.testemunhaADoc || '-'}</Typography>
              </Grid>
              <Grid item md={4} sm={4} xs={12}>
                <Typography variant="body2" color="textSecondary">
                  <b>ÓRGÃO EXPEDIDOR</b>
                </Typography>
                <Typography variant="body1">{formValues.testemunhaAOrgaoExpedidor || '-'}</Typography>
              </Grid>
            </>
          )}

          {formValues.testemunhaB && (
            <>
              <Grid item md={12} sm={12} xs={12}>
                <Typography variant="body2" color="textSecondary">
                  <b>TESTEMUNHA 2</b>
                </Typography>
                <Typography variant="body1">{formValues.testemunhaB}</Typography>
              </Grid>
              <Grid item md={4} sm={4} xs={12}>
                <Typography variant="body2" color="textSecondary">
                  <b>TIPO DE DOCUMENTO</b>
                </Typography>
                <Typography variant="body1">{formValues.testemunhaBTipoDoc || '-'}</Typography>
              </Grid>
              <Grid item md={4} sm={4} xs={12}>
                <Typography variant="body2" color="textSecondary">
                  <b>DOCUMENTO</b>
                </Typography>
                <Typography variant="body1">{formValues.testemunhaBDoc || '-'}</Typography>
              </Grid>
              <Grid item md={4} sm={4} xs={12}>
                <Typography variant="body2" color="textSecondary">
                  <b>ÓRGÃO EXPEDIDOR</b>
                </Typography>
                <Typography variant="body1">{formValues.testemunhaBOrgaoExpedidor || '-'}</Typography>
              </Grid>
            </>
          )}
        </Grid>
      </ModalConfirmacao>
    </Page>
  )
}

InumarCadaver.propTypes = {
  route: PropTypes.shape({
    titulo: PropTypes.string.isRequired,
    secao: PropTypes.string,
    origem: PropTypes.string,
  }).isRequired,
}
